import { useEffect, useRef, memo, useState } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { useAppStore } from '../../stores/appStore'
import { shallow } from 'zustand/shallow'
import { normalizeAssetUrl, buildApiUrl } from '../../config'
import { createNormalizedFBXLoader } from './fbxLoaderUtils'
import { getMaterialTextureRepeatMultiplier } from '../../utils/textileRepeat'
import { unifyRepeatsFromBase, applyPBR, loadTextureMaps, swapMeshUV, calculateUVAwareRepeat } from './textureUtils'
import { isSaturnOttoman } from './ottomanSaturn'

export { isSaturnOttoman }

// Hardcoded override for Saturn ottoman legs based on bed's selected leg ID
// Format: { bedLegId: ottomanLegId }
// If bed has this leg ID selected, use this leg ID for Saturn ottoman instead
export const SATURN_LEG_OVERRIDE_BY_BED_LEG: Record<number, number> = {
  88: 95,
  89: 96,
  90: 94,
  9:66,
  68:56,
  81:64,
  91:96,
  92:94,
  93:95,
  // Flying Optics → Round slim black (4-leg), matching height
  102: 56, // flying-optics-14-cm → round-slim-legs-black-steel-14-cm
  105: 72, // flying-optics-18-cm → round-slim-legs-black-steel-18-cm-4
}

// Hardcoded override for Saturn ottoman legs (legacy format, kept for backward compatibility)
// Format: { ottomanName: { legUrl: string, legTexture: any, legTypeId?: number } }
export const SATURN_LEG_OVERRIDE: Record<string, { legUrl?: string; legTexture?: any; legTypeId?: number }> = {
  // Example override (commented out - defaults to bed legs):
  // 'saturn': { legUrl: 'custom-leg-url.fbx', legTexture: null, legTypeId: 1 }
}

// Hook to get the appropriate leg selection for Saturn ottoman
export function useOttomanLegSelection(isSaturn: boolean) {
  const bedConfig = useAppStore((s) => s.bedConfig)
  const bedSlugFromStore = useAppStore((s) => s.bed)
  const [ottomanLegSelection, setOttomanLegSelection] = useState<any>(null)
  const [ottomanLiftY, setOttomanLiftY] = useState(0)

  useEffect(() => {
    if (!isSaturn || !bedConfig.ottoman?.three_d_model?.file_url) {
      setOttomanLegSelection(null)
      setOttomanLiftY(0)
      return
    }

    // Check if we should override based on bed's selected leg ID
    const bedLegId = bedConfig.legs?.id != null ? Number(bedConfig.legs.id) : null
    const bedLegTypeId = (bedConfig.legs as any)?.legTypeId as number | undefined
    const overrideLegId = bedLegId != null ? SATURN_LEG_OVERRIDE_BY_BED_LEG[bedLegId] : null

    const fetchLegFromApi = async (targetLegId: number | null, preferNonBase: boolean) => {
      try {
        const { market, lang } = useAppStore.getState()
        // Prefer URL/store bed slug (language-correct). bed-config variation.slug is often slug_en
        // (e.g. majestic-mc26-continental) which 404s under lang=nb (kontinental).
        const bedSlug = bedSlugFromStore || bedConfig.bed.variation?.slug
        if (!bedSlug) {
          setOttomanLegSelection(bedConfig.legs)
          return
        }

        const bedWidth = bedConfig.bed.selectedSize || 180
        const response = await fetch(
          buildApiUrl(`bed-variations/${bedSlug}/legs/?market=${market}&lang=${lang}&width_cm=${bedWidth}`)
        )
        if (!response.ok) {
          console.warn('[Ottoman] legs fetch failed', response.status, bedSlug)
          setOttomanLegSelection(bedConfig.legs)
          return
        }
        const legsData = await response.json()
        const legTypes = legsData.leg_types || {}

        let chosenLeg: any = null
        let chosenType: any = null

        if (targetLegId) {
          const targetId = Number(targetLegId)
          for (const legType of Object.values(legTypes) as any[]) {
            const leg = legType.legs?.find((l: any) => Number(l.id) === targetId)
            if (leg && leg.three_d_model) {
              chosenLeg = leg
              chosenType = legType
              break
            }
          }
        }

        if (!chosenLeg && preferNonBase) {
          for (const legType of Object.values(legTypes) as any[]) {
            if (legType.id === 3) continue
            const leg = Array.isArray(legType.legs) && legType.legs.length > 0 ? legType.legs[0] : null
            if (leg && leg.three_d_model) {
              chosenLeg = leg
              chosenType = legType
              break
            }
          }
        }

        if (chosenLeg && chosenType) {
          const slots = (chosenLeg.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: any[] }>
          const firstSlot = slots[0]
          const firstTexture = firstSlot?.textures?.[0] || null
          const legSelection = {
            id: chosenLeg.id,
            three_d_model: chosenLeg.three_d_model,
            currentTexture: firstTexture,
            legTypeId: chosenType.id,
            edge_inset: chosenLeg.edge_inset,
          }
          setOttomanLegSelection(legSelection)

          const loader = createNormalizedFBXLoader()
          loader.load(normalizeAssetUrl(chosenLeg.three_d_model.file_url), (legModel) => {
            const legBox = new THREE.Box3().setFromObject(legModel)
            const legHeight = (legBox.max.y - legBox.min.y) * 0.05
            setOttomanLiftY(legHeight)
          })

          return
        }

        // Fall back to bed legs if not found
        setOttomanLegSelection(bedConfig.legs)
        if (bedConfig.legs?.three_d_model?.file_url) {
          const loader = createNormalizedFBXLoader()
          loader.load(normalizeAssetUrl(bedConfig.legs.three_d_model.file_url), (legModel) => {
            const legBox = new THREE.Box3().setFromObject(legModel)
            const legHeight = (legBox.max.y - legBox.min.y) * 0.05
            setOttomanLiftY(legHeight)
          })
        } else {
          setOttomanLiftY(0)
        }
      } catch (err) {
        console.warn('[Ottoman] Failed to fetch ottoman legs:', err)
        setOttomanLegSelection(bedConfig.legs)
        if (bedConfig.legs?.three_d_model?.file_url) {
          const loader = createNormalizedFBXLoader()
          loader.load(normalizeAssetUrl(bedConfig.legs.three_d_model.file_url), (legModel) => {
            const legBox = new THREE.Box3().setFromObject(legModel)
            const legHeight = (legBox.max.y - legBox.min.y) * 0.05
            setOttomanLiftY(legHeight)
          })
        } else {
          setOttomanLiftY(0)
        }
      }
    }

    if (overrideLegId) {
      // Use explicit override mapping from bed leg ID to ottoman leg ID
      fetchLegFromApi(overrideLegId, false)
    } else {
      const isBedBase = bedLegTypeId === 3

      if (isBedBase) {
        // Bed is using a base; Saturn ottoman should instead use a non-base leg
        fetchLegFromApi(null, true)
      } else {
        const ottomanName = bedConfig.ottoman?.name || ''
        const ottomanLegOverride = SATURN_LEG_OVERRIDE[ottomanName.toLowerCase()]
        const selection = !ottomanLegOverride
          ? bedConfig.legs // Default: use bed legs
          : ottomanLegOverride.legUrl
            ? {
                three_d_model: { file_url: ottomanLegOverride.legUrl || '' },
                currentTexture: ottomanLegOverride.legTexture || null,
                legTypeId: ottomanLegOverride.legTypeId,
              }
            : null

        setOttomanLegSelection(selection)

        if (selection?.three_d_model?.file_url) {
          const loader = createNormalizedFBXLoader()
          loader.load(normalizeAssetUrl(selection.three_d_model.file_url), (leg) => {
            const legBox = new THREE.Box3().setFromObject(leg)
            const legHeight = (legBox.max.y - legBox.min.y) * 0.05
            setOttomanLiftY(legHeight)
          })
        } else {
          setOttomanLiftY(0)
        }
      }
    }
  }, [
    isSaturn,
    bedSlugFromStore,
    bedConfig.ottoman?.three_d_model?.file_url,
    bedConfig.legs?.id,
    bedConfig.legs?.three_d_model?.file_url,
    bedConfig.bed.variation?.slug,
    bedConfig.bed.selectedSize,
  ])

  return { ottomanLegSelection, ottomanLiftY }
}

function OttomanCmp({ ottomanUrl, texture, useUvChecker, rotateQuarterTurns, swapUV, bedLiftY, ottomanLiftY }: { ottomanUrl: string; texture: any; useUvChecker: boolean; rotateQuarterTurns: number; swapUV: boolean; bedLiftY: number; ottomanLiftY?: number }) {
  const ottoman = useLoader(FBXLoader, ottomanUrl)
  const ottomanBox = new THREE.Box3().setFromObject(ottoman)
  const ottomanCenter = ottomanBox.getCenter(new THREE.Vector3())
  const ottomanMin = ottomanBox.min
  const BASE_SCALE = 0.05
  const ottomanYBase = -ottomanBox.min.y * BASE_SCALE
  
  const ottomanConfig: any = useAppStore((s) => s.bedConfig.ottoman) as any
  const ottomanId = ottomanConfig?.id as number | undefined
  const isSaturn = isSaturnOttoman(ottomanConfig)
  const lastLoggedOttomanId = useRef<number | null>(null)
  
  useEffect(() => {
    const id: number | null = ottomanConfig?.id ?? null
    if (id && id !== lastLoggedOttomanId.current) {
      lastLoggedOttomanId.current = id
    }
  }, [ottomanConfig?.id])

  const bedBox = (window as any).__bed_bbox__
  if (!bedBox || bedBox.isEmpty()) return null as any

  // Position ottoman at the foot of the bed (opposite side from headboard)
  // All ottomans are always on the floor, unless ottomanLiftY is provided (for Saturn with legs)
  const bedWidth = bedBox.max.x - bedBox.min.x
  const ottomanX = bedBox.min.x + bedWidth / 2
  const ottomanZ = bedBox.max.z + 1.0 // Gap from foot of bed (opposite end from headboard)
  const ottomanY = ottomanYBase + (ottomanLiftY || 0) // Lift by leg height if provided

  const clone = ottoman.clone()
  clone.scale.setScalar(BASE_SCALE)
  clone.position.set(-ottomanCenter.x * BASE_SCALE, -ottomanMin.y * BASE_SCALE, -ottomanCenter.z * BASE_SCALE)
  clone.updateMatrixWorld(true)

  // Store ottoman bounding box globally for legs positioning (in world space)
  const ottomanBoxScaled = new THREE.Box3().setFromObject(clone)
  // Transform to world space by adding the ottoman's position
  ottomanBoxScaled.min.add(new THREE.Vector3(ottomanX, ottomanY, ottomanZ))
  ottomanBoxScaled.max.add(new THREE.Vector3(ottomanX, ottomanY, ottomanZ))
  ;(window as any).__ottoman_bbox__ = ottomanBoxScaled

  if (texture?.base_color_url || texture?.base_color_urls) {
    clone.traverse((child: any) => {
      if (child.isMesh && child.material) {
        try {
          child.geometry = (child.geometry as THREE.BufferGeometry).clone()
          if (child.geometry && child.geometry.userData) {
            delete child.geometry.userData._origUV
          }
        } catch {}
        swapMeshUV(child, !!swapUV, `ottoman-${ottomanId}`)
        const mat = new THREE.MeshStandardMaterial()
        mat.color = new THREE.Color(0xffffff)
        ;(mat as any).emissive?.setRGB?.(0, 0, 0)
        ;(mat as any).vertexColors = false
        const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
        
        const loader = new THREE.TextureLoader()
        const maps = loadTextureMaps(texture, loader)
        
        // Non-Saturn ottomans should map like tables/legs (wood-like, no textile scaling)
        // Saturn ottoman should map like textiles (reuse bed textile scaling)
        const bedConfigState = useAppStore.getState().bedConfig
        const multiplier = getMaterialTextureRepeatMultiplier(bedConfigState.textile?.textures, {
          textureId: bedConfigState.textile?.currentTexture?.id,
        })
        const bedFinalRepeat = (window as any).__bed_textile_final_repeat__

        let repeat: { x: number; y: number } | undefined
        let materialType: any = (texture as any)?.material_type || (texture as any)?.materialType || 'wood'

        if (isSaturn) {
          // Textile mapping: follow bed's final repeat so ottoman textile density matches the bed
          let baseRepeat = 1.0
          if (bedFinalRepeat && bedFinalRepeat.x && bedFinalRepeat.y) {
            baseRepeat = (bedFinalRepeat.x + bedFinalRepeat.y) / 2
          }
          repeat = {
            x: baseRepeat * multiplier,
            y: baseRepeat * multiplier,
          }
          materialType = 'textile'
        } else {
          // Wood-like mapping: rely on asset UVs, no extra scaling beyond a neutral repeat of 1
          repeat = undefined
          materialType = materialType || 'wood'
        }

        if (maps.base) {
          maps.base.flipY = false
          // @ts-ignore
          if ((THREE as any).SRGBColorSpace) {
            // @ts-ignore
            maps.base.colorSpace = (THREE as any).SRGBColorSpace
          }
          maps.base.wrapS = maps.base.wrapT = THREE.RepeatWrapping
          if ((maps.base as any).center && typeof (maps.base as any).center.set === 'function') {
            ;(maps.base as any).center.set(0.5, 0.5)
          }
        }
        
        applyPBR({
          mat,
          origMat: child.material,
          type: materialType,
          meshName: child.name,
          maps,
          rotateTurns: turns,
          hasUV2: !!child.geometry.attributes.uv2,
          repeat,
          offset: { x: 0, y: 0 },
        })
        unifyRepeatsFromBase(mat as any)
        child.material = mat
      }
    })
  }

  clone.traverse((child: any) => {
    if (child.isMesh) {
      child.castShadow = true
      child.receiveShadow = true
    }
  })

  return (
    <group position={[ottomanX, ottomanY, ottomanZ]}>
      <primitive object={clone} />
    </group>
  )
}

const Ottoman = memo(OttomanCmp)
export default Ottoman
